import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, FileText, Building2, Calendar, Activity, Loader2, ShieldX, ChevronRight, Building, UserPlus, Coins, Inbox } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const [counts, setCounts] = useState({
    users: 0,
    investors: 0,
    grants: 0,
    events: 0,
    pharma: 0,
    accessRequests: 0,
    pendingContributions: 0,
    programmeEnquiries: 0,
    newProgrammeEnquiries: 0
  });

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (isAdmin === true) {
      fetchCounts();
    }
  }, [isAdmin]);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) {
        navigate("/auth");
        return;
      }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
        if (!data.is_admin) {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    } catch (error) {
      navigate("/auth");
    }
  };

  const fetchCounts = async () => {
    try {
      // Fetch all counts in parallel
      const [statsRes, investorRes, grantsRes, eventsRes, pharmaRes, accessRes, contributionsRes, programmeRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/investor-stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/funding-opportunities`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/events`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/pharma-stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/access-requests?status=pending`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/contributions/stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/programme-enquiries`, { credentials: "include" })
      ]);

      const stats = statsRes.ok ? await statsRes.json() : {};
      const investors = investorRes.ok ? await investorRes.json() : {};
      const grants = grantsRes.ok ? await grantsRes.json() : {};
      const events = eventsRes.ok ? await eventsRes.json() : {};
      const pharma = pharmaRes.ok ? await pharmaRes.json() : {};
      const accessRequests = accessRes.ok ? await accessRes.json() : {};
      const contributions = contributionsRes.ok ? await contributionsRes.json() : {};
      const programme = programmeRes.ok ? await programmeRes.json() : { counts: {} };

      setCounts({
        users: stats.total_users || 0,
        investors: investors.total_firms || 0,
        grants: grants.total || 0,
        events: events.total || 0,
        pharma: pharma.total_pharma || 0,
        accessRequests: accessRequests.total || 0,
        pendingContributions: contributions.pending || 0,
        programmeEnquiries: (programme.counts && programme.counts.total) || 0,
        newProgrammeEnquiries: (programme.counts && programme.counts.new) || 0
      });
    } catch (error) {
      console.error("Failed to fetch counts:", error);
    } finally {
      setLoading(false);
    }
  };

  if (isAdmin === null || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">
                You don't have permission to access the admin panel.
              </p>
              <Button onClick={() => navigate("/dashboard")} className="btn-secondary">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const adminCards = [
    {
      title: "Access Requests",
      count: counts.accessRequests,
      icon: UserPlus,
      color: "amber",
      bgColor: "bg-amber-100",
      textColor: "text-amber-600",
      borderColor: "hover:border-amber-300",
      path: "/admin/access-requests",
      description: "Pending approvals",
      highlight: counts.accessRequests > 0
    },
    {
      title: "Contributions",
      count: counts.pendingContributions,
      icon: Coins,
      color: "orange",
      bgColor: "bg-orange-100",
      textColor: "text-orange-600",
      borderColor: "hover:border-orange-300",
      path: "/admin/contributions",
      description: "User intel reviews",
      highlight: counts.pendingContributions > 0
    },
    {
      title: "Programme Enquiries",
      count: counts.programmeEnquiries,
      icon: Inbox,
      color: "cyan",
      bgColor: "bg-cyan-100",
      textColor: "text-cyan-600",
      borderColor: "hover:border-cyan-300",
      path: "/admin/programme-enquiries",
      description: counts.newProgrammeEnquiries > 0
        ? `${counts.newProgrammeEnquiries} new to review`
        : "Operon-powered Programmes",
      highlight: counts.newProgrammeEnquiries > 0
    },
    {
      title: "Users",
      count: counts.users,
      icon: Users,
      color: "teal",
      bgColor: "bg-teal-100",
      textColor: "text-teal-600",
      borderColor: "hover:border-teal-300",
      path: "/admin/users",
      description: "Platform users"
    },
    {
      title: "Investors",
      count: counts.investors,
      icon: Building2,
      color: "blue",
      bgColor: "bg-blue-100",
      textColor: "text-blue-600",
      borderColor: "hover:border-blue-300",
      path: "/admin/investors",
      description: "VC firms & contacts"
    },
    {
      title: "Pharma",
      count: counts.pharma,
      icon: Building,
      color: "indigo",
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-600",
      borderColor: "hover:border-indigo-300",
      path: "/admin/pharma",
      description: "Pharma companies"
    },
    {
      title: "Grants",
      count: counts.grants,
      icon: FileText,
      color: "emerald",
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-600",
      borderColor: "hover:border-emerald-300",
      path: "/admin/funding-opportunities",
      description: "Funding opportunities"
    },
    {
      title: "Events",
      count: counts.events,
      icon: Calendar,
      color: "purple",
      bgColor: "bg-purple-100",
      textColor: "text-purple-600",
      borderColor: "hover:border-purple-300",
      path: "/admin/events",
      description: "Biotech conferences"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage platform data and settings</p>
        </div>

        {/* Data Management Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {adminCards.map((card) => (
            <Card
              key={card.title}
              className={`border cursor-pointer transition-all hover:shadow-lg ${card.borderColor} ${card.highlight ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
              onClick={() => navigate(card.path)}
              data-testid={`admin-card-${card.title.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2.5 rounded-xl ${card.bgColor}`}>
                    <card.icon className={`w-6 h-6 ${card.textColor}`} />
                  </div>
                  {card.highlight ? (
                    <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-medium">New</span>
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-3xl font-bold text-slate-900 font-mono">{card.count}</p>
                  <p className="font-medium text-slate-900">{card.title}</p>
                  <p className="text-xs text-slate-500">{card.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Strategy Questions Button */}
        <div className="border-t border-slate-200 pt-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Configuration</h2>
          <Card 
            className="border border-slate-200 cursor-pointer transition-all hover:shadow-md hover:border-teal-300 max-w-md"
            onClick={() => navigate("/admin/strategy-questions")}
            data-testid="strategy-questions-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal-100">
                  <Activity className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Strategy Questions</p>
                  <p className="text-xs text-slate-500">Manage diagnosis & strategy fields</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
